import axios from 'axios';
import { notification } from 'antd';
import { getStoredAuthToken, normalizeAuthHeader, reauthorize } from './auth';

declare module 'axios' {
    export interface AxiosRequestConfig {
        /**
         * Skip the error toast for this request. For side blocks that already hide themselves on
         * failure (the dashboard's referral link): a missing or failing endpoint there must not
         * interrupt the subscriber with "Xatolik" every time the screen opens.
         */
        silent?: boolean;
    }
}

const storedToken = getStoredAuthToken();

const axiosServices = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3010/',
    // baseURL: '/api',
    withCredentials: true,
    headers: {
        'X-Bot-Id': '1',
        'Accept-Language': 'uz-UZ',
        ...(storedToken ? { Authorization: normalizeAuthHeader(storedToken) } : {})
    }
});

// ==============================|| AXIOS - FOR MOCK SERVICES ||============================== //

// The uz translation of Yii's "Login Required": the session cookie died (expired
// in Redis, or the container restarted before Redis sessions existed). Distinct
// from a real permission 403, which must not trigger a re-auth loop.
const SESSION_LOST_MESSAGE = 'Kirish talab qilinadi.';

axiosServices.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const config = error.config;
        const status = error.response?.status;
        const message = error.response?.data?.body?.message;

        const sessionLost = status === 401 || (status === 403 && message === SESSION_LOST_MESSAGE);
        const isAuthRequest = typeof config?.url === 'string' && config.url.includes('/auth/');

        if (sessionLost && config && !config._retried && !isAuthRequest) {
            config._retried = true;

            try {
                const botId = config.headers?.['X-Bot-Id'] || axiosServices.defaults.headers.common['X-Bot-Id'] || '1';
                await reauthorize(String(botId));

                const token = getStoredAuthToken();
                if (token) {
                    config.headers.Authorization = normalizeAuthHeader(token);
                }

                return axiosServices(config);
            } catch (reauthError) {
                // Re-auth failed too — fall through to the normal error handling.
            }
        }

        if (status === 401 && window.location.pathname !== '/') {
            window.location.pathname = '/';
        }

        const errorMessage = error.response?.data?.body?.message ||
                           error.response?.data?.message ||
                           error.message ||
                           'Xatolik yuz berdi. Iltimos qayta urinib ko\'ring.';

        if (!config?.silent) {
            notification.error({
                message: 'Xatolik',
                description: errorMessage,
                placement: 'bottom',
                duration: 4,
            });
        }

        return Promise.reject((error.response && error.response.data) || 'Wrong Services');
    }
);

axiosServices.interceptors.request.use((request) => {
    const token = getStoredAuthToken();

    if (token) {
        const headers = request.headers as any;
        headers.Authorization = normalizeAuthHeader(token);
    }

    return request;
});

export default axiosServices;
